import { Button, Card, Checkbox, Datepicker, WeekStart } from 'flowbite-react';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QueryResult } from '../../components/Alerts.tsx';
import { useBuildingContext } from '../../components/BuildingContext.tsx';
import ProtectedRoute from '../../components/ProtectedRoute.tsx';
import RequiresBuilding from '../../components/RequiresBuilding.tsx';
import RequiresFeature, { Features } from '../../components/RequiresFeature.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useAppDispatch, useAppSelector } from '../../lib/hooks.ts';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import {
    LocalDateTime,
    ReservationView,
    useAssignParkingSpotsMutation
} from '../../lib/model/kantoordagApi.ts';
import { kantoordagParkingViewStreamingApi, useGetParkingViewQuery } from '../../lib/model/kantoordag/kantoordagParkingViewStreamingApi.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';

export default function Parking() {
    return (
        <RequiresBuilding>
            <ParkingInternal/>
        </RequiresBuilding>
    )
}

enum Mutation {
    NoChange,
    Added,
    Removed,
}

function ParkingInternal() {
    const dispatch = useAppDispatch()
    const user = useAppSelector(state => state.user.user)
    const selectedBuilding = useBuildingContext();
    const date = DateTime.fromISO(useAppSelector(state => state.date.date))
    const [parkingUsers, setParkingUsers] = useState<Array<ReservationView>>([])
    const [mutations, setMutations] = useState<Map<string, Mutation>>(new Map<string, Mutation>())
    const { t, i18n } = useTranslation("parking");
    const getParkingQuery = useGetParkingViewQuery({
        building: selectedBuilding?.id ?? "",
        date: date.toISODate()!,
    })

    const [parkingMutation, result] = useAssignParkingSpotsMutation()

    function dateChanged(date: Date | null) {
        if (date) {
            dispatch(selectDate(DateTime.fromJSDate(date)))
        }
    }

    function cancel() {
        setMutations(new Map())
    }

    function save() {
        parkingMutation({
            building: selectedBuilding?.name ?? "",
            datum: date.toISODate()!,
            body: parkingUsers
                .filter(reservation => reservation.reserved)
                .map(reservation => reservation.userId)
        }).unwrap().then(() => {
            toast.success(t("saving_success"))
            // Optimistically write the saved state into the RTK Query cache so that
            // clearing mutations doesn't revert the display to stale Firestore data
            // while the server projection propagates.
            dispatch(kantoordagParkingViewStreamingApi.util.updateQueryData(
                'get',
                { date: date.toISODate()!, building: selectedBuilding?.id ?? "" },
                () => parkingUsers,
            ))
            setMutations(new Map())
        }).catch(() => {
            toastError({api: result, message: t("saving_failed")})
        })
    }

    function formatReservationDate(reservationDate: LocalDateTime) {
        const dateTime = DateTime.fromISO(reservationDate)
        return dateTime.toLocaleString(DateTime.DATETIME_SHORT)
    }

    const mergeMutations = useCallback((users: Array<ReservationView>) : Array<ReservationView> => {
        return users.map(user => {
            let reserved;

            switch (mutations.get(user.userId)) {
                case Mutation.NoChange:
                default:
                    reserved = user.reserved
                    break;
                case Mutation.Added:
                    reserved = true
                    break;
                case Mutation.Removed:
                    reserved = false
                    break;
            }

            return {
                ...user,
                reserved
            }
        })
    }, [mutations])

    const fillParkingUsers = useCallback(() => {
        if (getParkingQuery.isSuccess) {
            setParkingUsers(mergeMutations(getParkingQuery.data))
        }
    }, [getParkingQuery, mergeMutations])

    const checkboxChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>, userId: string)=> {
        const newMap = new Map(mutations.entries())
        if (e.target.checked) {
            switch (newMap.get(userId)) {
                case Mutation.NoChange:
                default:
                    newMap.set(userId, Mutation.Added)
                    break;
                case Mutation.Added:
                    // No change
                    break
                case Mutation.Removed:
                    newMap.set(userId, Mutation.NoChange)
                    break
            }
        } else {
            switch (newMap.get(userId)) {
                case Mutation.NoChange:
                default:
                    newMap.set(userId, Mutation.Removed)
                    break;
                case Mutation.Added:
                    newMap.set(userId, Mutation.NoChange)
                    break
                case Mutation.Removed:
                    // No change
                    break
            }
        }
        console.log("Mutations: " + JSON.stringify(newMap))
        setMutations(newMap)
        fillParkingUsers();
    }, [fillParkingUsers, mutations])

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => fillParkingUsers(), [fillParkingUsers]);

    const isAdmin = userHasRole(user, ["ADMIN"])
    const checkboxDisabled = !isAdmin || getParkingQuery.isLoading

    return (
        <ProtectedRoute authenticated={true}>
            <RequiresFeature feature={Features.PARKING}>
            <Datepicker
                onChange={dateChanged}
                language={i18n.language}
                value={date.toJSDate()}
                weekStart={WeekStart.Monday}
            />
            <Card className={"flex w-full"}>
                <QueryResult query={getParkingQuery} message={t("loading_failed")}>
                    {
                        getParkingQuery.data !== undefined ?
                            (
                                <>
                                    {
                                        parkingUsers.map(reservation =>
                                            <div key={reservation.userId} className={"flex flex-row"}>
                                                <Checkbox
                                                       checked={reservation.reserved}
                                                       disabled={checkboxDisabled}
                                                       className={"mr-2"}
                                                       onChange={e => checkboxChanged(e, reservation.userId)}/>
                                                {reservation.userName} - {t(reservation.periode)} ({formatReservationDate(reservation.reservationDate)})
                                            </div>
                                        )
                                    }
                                    {
                                        getParkingQuery.data.length == 0 ?
                                            <span>{t("no_signups")}</span>
                                            : null
                                    }
                                </>
                            )
                            : null
                    }
                    {
                        userHasRole(user!, "ADMIN") ?
                            (
                                <div className={"flex flex-row items-center"}>
                                    <Button onClick={cancel} color="secondary" className={"m-1"}>{t("cancel")}</Button>
                                    <Button onClick={save} color="primary" className={"m-1"}>{t("save")}</Button>
                                    <Link to={"/parking/illegal"} className={"link"}><Trans t={t}>illegal_parking</Trans></Link>
                                </div>
                            )
                            : null
                    }
                </QueryResult>
            </Card>
            </RequiresFeature>
        </ProtectedRoute>
            )
}
